import {useEffect, useMemo, useCallback} from "react";
import {useHits, useSearchBox, type UseHitsProps} from 'react-instantsearch';
import {useConfigContext} from "@/ConfigProvider";
import {useInstantSearchUIContext} from "@/instantsearch/InstantSearchUIProvider";
import {ListUpdater} from "@/ListUpdater";
import ItemBox from "@/instantsearch/components/ItemBox";
import ItemlistElement from "@/instantsearch/components/ItemlistElement";
import {useProductTracking, useSearchTracking} from "@/hooks/analytics";
import type {AlgoliaProductAttributes} from "../../AlgoliaProductAttributes";

export function ItemList(props: UseHitsProps<AlgoliaProductAttributes>) {
    const {items, sendEvent} = useHits(props);
    const {query} = useSearchBox();
    const {listRef, isActive, viewMode} = useInstantSearchUIContext();
    const {algoliaConfig: {siteCode, languageCode, isCatalog, isSearch, isFundingPage}} = useConfigContext();

    const { trackItemList } = useProductTracking();
    useSearchTracking({ query, items, isSearch });

    const effectiveViewMode = isFundingPage ? 'list' : viewMode;

    const renderedItems = useMemo(
        () => items.map(hit =>
            effectiveViewMode === 'list'
                ? <ItemlistElement hit={hit} sendEvent={sendEvent} key={hit.objectID}/>
                : <ItemBox hit={hit} sendEvent={sendEvent} key={hit.objectID}/>
        ),
        [items, effectiveViewMode, sendEvent]
    );

    const runListUpdater = useCallback(() => {
        if (typeof window === 'undefined') return;
        if (!(window as any).__APP_HYDRATED__) return;
        if (!listRef.current || items.length === 0) return;

        const id = requestAnimationFrame(() => {
            if (!listRef.current?.isConnected) return;

            const targetElements = listRef.current.querySelectorAll('.js-updateAlgoliaItemboxElement');
            if (targetElements.length === 0) return;

            try {
                ListUpdater(listRef.current!, siteCode, languageCode);

                const sendGAEventAfterPriceLoad = () => {
                    setTimeout(() => {
                        const loadedItems = listRef.current?.querySelectorAll('.js-updateAlgoliaItemboxElement.is-loaded');
                        if (loadedItems && loadedItems.length > 0) {
                            trackItemList(items, isSearch);
                        } else {
                            setTimeout(() => {
                                const retryLoadedItems = listRef.current?.querySelectorAll('.js-updateAlgoliaItemboxElement.is-loaded');
                                if (retryLoadedItems && retryLoadedItems.length > 0) {
                                    trackItemList(items, isSearch);
                                } else {
                                    trackItemList(items, isSearch);
                                }
                            }, 500);
                        }
                    }, 300);
                };

                sendGAEventAfterPriceLoad();
            } catch (e) {
                console.error('[ListUpdater] failed:', e);
            }
        });
        return () => cancelAnimationFrame(id);
    }, [listRef, items, effectiveViewMode, siteCode, languageCode, trackItemList, isSearch]);

    useEffect(() => {
        const alreadyHydrated = (window as any).__APP_HYDRATED__;

        if (alreadyHydrated) {
            const dispose = runListUpdater();
            return () => {
                if (typeof dispose === 'function') dispose();
            };
        }

        const onHydrated = () => {
            runListUpdater();
        };
        window.addEventListener('app:hydrated', onHydrated, { once: true });

        return () => {
            window.removeEventListener('app:hydrated', onHydrated);
        };
    }, [runListUpdater]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (!(window as any).__APP_HYDRATED__) return;
        return runListUpdater();
    }, [items, runListUpdater]);


    if (!renderedItems) return <></>;

    return (
        <ol
            className={`algoliaSearchItemList ${isActive ? "is-active" : ""} ${effectiveViewMode === 'list' ? "itemlist itemlist--1" : "itemboxList"}`}
            tabIndex={-1}
            ref={listRef}
        >
            {renderedItems}
        </ol>
    );
}